import { useState, useEffect, useRef, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RefreshCw, Loader2, Mail, UserPlus, Briefcase, FileText, Edit3, Trash2, Calendar, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { AIWordingButton } from "./AIWordingButton";

const API = `${window.location.origin}/api`;

const serviceLabels = {
  carpentry: "Carpentry",
  "general-repairs": "General Repairs",
  "furniture-builds": "Furniture Builds",
  "cnc-factory": "CNC/Factory Work"
};

// Map job status to pipeline stages
const jobStatusToPipelineStage = {
  'planned': 'scheduled',
  'in-progress': 'in_progress',
  'completed': 'completed'
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (e) { return dateStr; }
};

export default function PipelineTab({ authHeaders, onConvertToCustomer }) {
  const [pipelineStages, setPipelineStages] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [selectedPipelineEnquiry, setSelectedPipelineEnquiry] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isPipelineDetailOpen, setIsPipelineDetailOpen] = useState(false);
  const [isJobDetailOpen, setIsJobDetailOpen] = useState(false);
  const [isEditJobOpen, setIsEditJobOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [isEditCustomerOpen, setIsEditCustomerOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [isSavingCustomer, setIsSavingCustomer] = useState(false);
  const [isEditEnquiryOpen, setIsEditEnquiryOpen] = useState(false);
  const [editingEnquiry, setEditingEnquiry] = useState(null);
  const [isSavingEnquiry, setIsSavingEnquiry] = useState(false);
  const [isNotifyDialogOpen, setIsNotifyDialogOpen] = useState(false);
  const [notifyType, setNotifyType] = useState('customer');
  const [notifyMessage, setNotifyMessage] = useState('');
  const [isSendingNotification, setIsSendingNotification] = useState(false);
  const [isSavingJob, setIsSavingJob] = useState(false);
  const [viewMode, setViewMode] = useState('all'); // 'all', 'enquiries', 'jobs'
  const fetchingRef = useRef(false);

  const fetchPipeline = async () => {
    if (fetchingRef.current) {
      return;
    }
    fetchingRef.current = true;
    setPipelineLoading(true);
    try {
      const [stagesRes, enquiriesRes, customersRes] = await Promise.all([
        axios.get(`${API}/admin/pipeline/stages`, authHeaders),
        axios.get(`${API}/admin/enquiries`, authHeaders),
        axios.get(`${API}/admin/customers`, authHeaders)
      ]);
      setPipelineStages(stagesRes.data.stages);
      setEnquiries(enquiriesRes.data);
      
      // Extract jobs from customers with customer info
      const allJobs = [];
      customersRes.data.forEach(customer => {
        (customer.jobs || []).forEach((job, index) => {
          allJobs.push({
            ...job,
            // Use job.id if available, otherwise use index as fallback
            id: job.id || String(index),
            job_index: index,  // Always store the index for fallback
            customer_name: customer.name,
            customer_email: customer.email,
            customer_phone: customer.phone,
            customer_address: customer.address,
            customer_id: customer.id,
            type: 'job'
          });
        });
      });
      setJobs(allJobs);
    } catch (error) {
      console.error("Error fetching pipeline:", error);
      toast.error("Failed to load pipeline data");
    } finally {
      setPipelineLoading(false);
      fetchingRef.current = false;
    }
  };

  useEffect(() => {
    // Fetch pipeline data when component mounts and authHeaders is available
    const token = authHeaders?.headers?.Authorization;
    if (token && !fetchingRef.current) {
      fetchPipeline();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeaders?.headers?.Authorization]);

  const updateEnquiryStage = async (enquiryId, newStage, notes = null) => {
    try {
      const params = new URLSearchParams({ stage: newStage });
      if (notes) params.append('notes', notes);
      const response = await axios.patch(`${API}/admin/enquiries/${enquiryId}/stage?${params.toString()}`, {}, authHeaders);
      
      const stageName = pipelineStages.find(s => s.id === newStage)?.label || newStage;
      toast.success(`Stage updated to ${stageName}`);
      
      // Show additional notification if a job was created
      if (response.data.job_created) {
        toast.info("✅ Job automatically created! Check the Pipeline or Customers tab.", { duration: 5000 });
      }
      
      fetchPipeline();
      setIsPipelineDetailOpen(false);
    } catch (error) {
      console.error("Error updating stage:", error);
      toast.error("Failed to update stage");
    }
  };

  // Map pipeline stage back to job status
  const pipelineStageToJobStatus = {
    'scheduled': 'planned',
    'in_progress': 'in-progress',
    'completed': 'completed',
    'invoiced': 'completed',
    'paid': 'completed',
    'closed': 'completed',
    'rejected': 'rejected',
    'cancelled': 'cancelled'
  };

  // Update job stage (updates job status in customer record)
  const updateJobStage = async (customerId, jobId, newStage) => {
    try {
      const newStatus = pipelineStageToJobStatus[newStage] || 'planned';
      await axios.patch(`${API}/admin/customers/${customerId}/jobs/${jobId}`, 
        { status: newStatus },
        authHeaders
      );
      toast.success(`Job moved to ${pipelineStages.find(s => s.id === newStage)?.label || newStage}`);
      fetchPipeline();
      setIsJobDetailOpen(false);
    } catch (error) {
      console.error("Error updating job stage:", error);
      toast.error("Failed to update job stage");
    }
  };

  // Edit job
  const openEditJob = (job) => {
    setEditingJob({
      ...job,
      description: job.description || '',
      date: job.date || '',
      time_slot: job.time_slot || '09:00',
      duration: job.duration || 2,
      notes: job.notes || ''
    });
    setIsEditJobOpen(true);
  };

  const saveJob = async () => {
    if (!editingJob) return;
    setIsSavingJob(true);
    try {
      await axios.patch(
        `${API}/admin/customers/${editingJob.customer_id}/jobs/${editingJob.id}`,
        {
          description: editingJob.description,
          date: editingJob.date,
          time_slot: editingJob.time_slot,
          duration: editingJob.duration,
          notes: editingJob.notes,
          status: editingJob.status
        },
        authHeaders
      );
      toast.success("Job updated successfully");
      setIsEditJobOpen(false);
      setEditingJob(null);
      fetchPipeline();
    } catch (error) {
      console.error("Error saving job:", error);
      toast.error("Failed to save job");
    } finally {
      setIsSavingJob(false);
    }
  };

  // Delete job
  const deleteJob = async (customerId, jobId) => {
    if (!confirm("Are you sure you want to delete this job? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/customers/${customerId}/jobs/${jobId}`, authHeaders);
      toast.success("Job deleted");
      setIsJobDetailOpen(false);
      fetchPipeline();
    } catch (error) {
      console.error("Error deleting job:", error);
      toast.error("Failed to delete job");
    }
  };

  // Edit customer - opens from job detail
  const openEditCustomer = async (customerId) => {
    try {
      const response = await axios.get(`${API}/admin/customers/${customerId}`, authHeaders);
      const customer = response.data;
      setEditingCustomer({
        id: customer.id,
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        notes: customer.notes || '',
        source: customer.source || 'website'
      });
      setIsEditCustomerOpen(true);
    } catch (error) {
      console.error("Error fetching customer:", error);
      toast.error("Failed to load customer details");
    }
  };

  const saveCustomer = async () => {
    if (!editingCustomer) return;
    setIsSavingCustomer(true);
    try {
      await axios.patch(
        `${API}/admin/customers/${editingCustomer.id}`,
        {
          name: editingCustomer.name,
          email: editingCustomer.email,
          phone: editingCustomer.phone,
          address: editingCustomer.address,
          notes: editingCustomer.notes,
          source: editingCustomer.source
        },
        authHeaders
      );
      toast.success("Customer updated successfully");
      setIsEditCustomerOpen(false);
      setEditingCustomer(null);
      fetchPipeline();
    } catch (error) {
      console.error("Error saving customer:", error);
      toast.error("Failed to save customer");
    } finally {
      setIsSavingCustomer(false);
    }
  };

  // Delete customer
  const deleteCustomer = async (customerId) => {
    if (!confirm("Are you sure you want to delete this customer and ALL their jobs? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/customers/${customerId}`, authHeaders);
      toast.success("Customer deleted");
      setIsJobDetailOpen(false);
      setIsEditCustomerOpen(false);
      fetchPipeline();
    } catch (error) {
      console.error("Error deleting customer:", error);
      toast.error("Failed to delete customer");
    }
  };

  // Edit enquiry
  const openEditEnquiry = (enquiry) => {
    setEditingEnquiry({
      ...enquiry,
      name: enquiry.name || '',
      email: enquiry.email || '',
      phone: enquiry.phone || '',
      address: enquiry.address || '',
      service: enquiry.service || '',
      message: enquiry.message || '',
      notes: enquiry.notes || '',
      preferred_date: enquiry.preferred_date || ''
    });
    setIsEditEnquiryOpen(true);
    setIsPipelineDetailOpen(false);
  };

  const saveEnquiry = async () => {
    if (!editingEnquiry) return;
    setIsSavingEnquiry(true);
    try {
      await axios.patch(
        `${API}/admin/enquiries/${editingEnquiry.id}`,
        {
          name: editingEnquiry.name,
          email: editingEnquiry.email,
          phone: editingEnquiry.phone,
          address: editingEnquiry.address,
          service: editingEnquiry.service,
          message: editingEnquiry.message,
          notes: editingEnquiry.notes,
          preferred_date: editingEnquiry.preferred_date
        },
        authHeaders
      );
      toast.success("Enquiry updated");
      setIsEditEnquiryOpen(false);
      setEditingEnquiry(null);
      fetchPipeline();
    } catch (error) {
      console.error("Error saving enquiry:", error);
      toast.error("Failed to save enquiry");
    } finally {
      setIsSavingEnquiry(false);
    }
  };

  // Delete enquiry
  const deleteEnquiry = async (enquiryId) => {
    if (!confirm("Are you sure you want to delete this enquiry? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/enquiries/${enquiryId}`, authHeaders);
      toast.success("Enquiry deleted");
      setIsPipelineDetailOpen(false);
      setIsEditEnquiryOpen(false);
      fetchPipeline();
    } catch (error) {
      console.error("Error deleting enquiry:", error);
      toast.error("Failed to delete enquiry");
    }
  };

  const sendEnquiryNotification = async () => {
    if (!selectedPipelineEnquiry) return;
    setIsSendingNotification(true);
    try {
      const params = new URLSearchParams({ notification_type: notifyType });
      if (notifyMessage) params.append('message', notifyMessage);
      const res = await axios.post(`${API}/admin/enquiries/${selectedPipelineEnquiry.id}/notify?${params.toString()}`, {}, authHeaders);
      toast.success(res.data.message || "Notification sent!");
      setIsNotifyDialogOpen(false);
      setNotifyMessage('');
    } catch (error) {
      console.error("Error sending notification:", error);
      toast.error(error.response?.data?.detail || "Failed to send notification");
    } finally {
      setIsSendingNotification(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold font-heading">Customer Journey Pipeline</h2>
          <p className="text-sm text-muted-foreground">Track enquiries and jobs from first contact to completion</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex rounded-lg border overflow-hidden">
            {[
              { id: 'all', label: 'All' },
              { id: 'enquiries', label: 'Enquiries' },
              { id: 'jobs', label: 'Jobs' }
            ].map((mode) => (
              <button
                key={mode.id}
                onClick={() => setViewMode(mode.id)}
                className={`px-3 py-1.5 text-sm transition-colors ${
                  viewMode === mode.id 
                    ? 'bg-primary text-primary-foreground' 
                    : 'hover:bg-muted'
                }`}
              >
                {mode.label}
              </button>
            ))}
          </div>
          <Button onClick={fetchPipeline} variant="outline" size="sm" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Summary Stats */}
      <div className="flex gap-4 text-sm">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-blue-500" />
          <span className="text-muted-foreground">Enquiries:</span>
          <span className="font-medium">{enquiries.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <Briefcase className="h-4 w-4 text-green-500" />
          <span className="text-muted-foreground">Jobs:</span>
          <span className="font-medium">{jobs.length}</span>
        </div>
      </div>
      
      {pipelineLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="overflow-x-auto pb-4 -mx-4 px-4">
          <div className="flex gap-3 min-w-max">
            {pipelineStages
              .filter(stage => !['invoiced', 'paid'].includes(stage.id)) // Hide invoiced/paid stages - these are completed
              .map((stage) => {
              // Filter enquiries for this stage (exclude invoiced/paid enquiries from all views)
              const stageEnquiries = (viewMode === 'all' || viewMode === 'enquiries') 
                ? enquiries.filter(e => 
                    !['invoiced', 'paid'].includes(e.status) && ( // Exclude completed enquiries
                      (e.status === stage.id) || 
                      (stage.id === 'new' && (!e.status || e.status === 'pending'))
                    )
                  )
                : [];
              
              // Filter jobs for this stage (map job status to pipeline stage)
              const stageJobs = (viewMode === 'all' || viewMode === 'jobs')
                ? jobs.filter(j => {
                    const mappedStage = jobStatusToPipelineStage[j.status];
                    return mappedStage === stage.id;
                  })
                : [];
              
              const totalItems = stageEnquiries.length + stageJobs.length;
              
              return (
                <div 
                  key={stage.id}
                  className="w-72 flex-shrink-0 bg-muted/30 rounded-lg"
                  data-testid={`pipeline-stage-${stage.id}`}
                >
                  <div 
                    className="px-3 py-2 rounded-t-lg flex items-center justify-between"
                    style={{ backgroundColor: stage.color + '20', borderBottom: `3px solid ${stage.color}` }}
                  >
                    <span className="font-medium text-sm" style={{ color: stage.color }}>{stage.label}</span>
                    <Badge variant="secondary" className="text-xs">{totalItems}</Badge>
                  </div>
                  <div className="p-2 space-y-2 min-h-[200px] max-h-[60vh] overflow-y-auto">
                    {stageEnquiries.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        No enquiries
                      </div>
                    ) : (
                      stageEnquiries.map((enquiry) => (
                        <Card 
                          key={enquiry.id}
                          className="cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() => { setSelectedPipelineEnquiry(enquiry); setIsPipelineDetailOpen(true); }}
                          data-testid={`pipeline-card-${enquiry.id}`}
                        >
                          <CardContent className="p-3 space-y-2">
                            <div className="flex items-start justify-between">
                              <p className="font-medium text-sm line-clamp-1">{enquiry.name}</p>
                              <Badge variant="outline" className="text-[10px] shrink-0 ml-2">
                                {serviceLabels[enquiry.service] || enquiry.service}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground line-clamp-2">{enquiry.message}</p>
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                              <span>{formatDate(enquiry.created_at)}</span>
                              {enquiry.phone && <span>{enquiry.phone}</span>}
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                    {/* Render Jobs */}
                    {stageJobs.map((job) => (
                      <Card 
                        key={job.id}
                        className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-green-500"
                        onClick={() => { setSelectedJob(job); setIsJobDetailOpen(true); }}
                        data-testid={`pipeline-job-${job.id}`}
                      >
                        <CardContent className="p-3 space-y-2">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-1.5">
                              <Briefcase className="h-3.5 w-3.5 text-green-600" />
                              <p className="font-medium text-sm line-clamp-1">{job.customer_name}</p>
                            </div>
                            <Badge variant="outline" className="text-[10px] shrink-0 ml-2 bg-green-50 text-green-700 border-green-200">
                              Job
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-2">{job.description}</p>
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>{formatDate(job.date)}</span>
                            {job.is_all_day ? (
                              <span className="text-blue-600">All Day</span>
                            ) : (
                              <span>{job.time_slot || ''} {job.duration ? `(${job.duration}h)` : ''}</span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    {totalItems === 0 && (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        No items
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Job Detail Dialog */}
      <Dialog open={isJobDetailOpen} onOpenChange={setIsJobDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-start justify-between">
              <div>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-green-600" />
                  {selectedJob?.customer_name}
                </DialogTitle>
                <DialogDescription>
                  Job scheduled for {formatDate(selectedJob?.date)}
                </DialogDescription>
              </div>
              {/* Edit/Delete buttons prominently placed in header */}
              <div className="flex gap-2 ml-4">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => { openEditJob(selectedJob); setIsJobDetailOpen(false); }}
                  data-testid="edit-job-btn"
                >
                  <Edit3 className="h-4 w-4" />
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={() => deleteJob(selectedJob?.customer_id, selectedJob?.id)}
                  data-testid="delete-job-btn"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </DialogHeader>
          {selectedJob && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-muted-foreground">Customer</Label>
                  <p>{selectedJob.customer_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <Badge variant={selectedJob.status === 'completed' ? 'default' : 'outline'}>
                    {selectedJob.status}
                  </Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground">Date</Label>
                  <p>{formatDate(selectedJob.date)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Time</Label>
                  <p>{selectedJob.is_all_day ? 'All Day' : `${selectedJob.time_slot || 'Not set'} (${selectedJob.duration || 0}h)`}</p>
                </div>
                {selectedJob.customer_address && (
                  <div className="col-span-2">
                    <Label className="text-muted-foreground">Address</Label>
                    <p>{selectedJob.customer_address}</p>
                  </div>
                )}
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Description</Label>
                  <p className="text-sm">{selectedJob.description}</p>
                </div>
                {selectedJob.assigned_admins?.length > 0 && (
                  <div className="col-span-2">
                    <Label className="text-muted-foreground">Assigned To</Label>
                    <div className="flex gap-1 mt-1">
                      {selectedJob.assigned_admins.map((admin, i) => (
                        <Badge key={i} variant="outline">{admin.name}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Move to Stage - Same as Enquiries */}
              <div>
                <Label className="text-muted-foreground mb-2 block">Move to Stage</Label>
                <div className="flex flex-wrap gap-2">
                  {pipelineStages.filter(s => ['scheduled', 'in_progress', 'completed', 'invoiced', 'paid', 'closed', 'rejected', 'cancelled'].includes(s.id)).map((stage) => {
                    const currentStage = jobStatusToPipelineStage[selectedJob.status];
                    return (
                      <Button
                        key={stage.id}
                        size="sm"
                        variant={currentStage === stage.id ? "default" : "outline"}
                        onClick={() => updateJobStage(selectedJob.customer_id, selectedJob.id, stage.id)}
                        style={{ 
                          borderColor: stage.color,
                          backgroundColor: currentStage === stage.id ? stage.color : 'transparent',
                          color: currentStage === stage.id ? 'white' : stage.color
                        }}
                        className="text-xs"
                        data-testid={`job-stage-btn-${stage.id}`}
                      >
                        {stage.label}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Edit Customer Action */}
              <div className="pt-4 border-t">
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={() => { openEditCustomer(selectedJob.customer_id); setIsJobDetailOpen(false); }}
                  className="w-full"
                  data-testid="edit-customer-btn"
                >
                  <UserPlus className="h-4 w-4 mr-1" />
                  Edit Customer Details
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Job Dialog */}
      <Dialog open={isEditJobOpen} onOpenChange={setIsEditJobOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Edit3 className="h-5 w-5" />
              Edit Job
            </DialogTitle>
            <DialogDescription>
              Update job details for {editingJob?.customer_name}
            </DialogDescription>
          </DialogHeader>
          {editingJob && (
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Description</Label>
                  <AIWordingButton value={editingJob.description} onApply={(t) => setEditingJob({...editingJob, description: t})} context="a job description in the jobs pipeline" testId="ai-edit-job-description" />
                </div>
                <Textarea
                  value={editingJob.description}
                  onChange={(e) => setEditingJob({...editingJob, description: e.target.value})}
                  placeholder="Job description..."
                  className="min-h-[80px]"
                  data-testid="edit-job-description"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><Calendar className="h-4 w-4" /> Date</Label>
                  <Input
                    type="date"
                    value={editingJob.date || ''}
                    onChange={(e) => setEditingJob({...editingJob, date: e.target.value})}
                    data-testid="edit-job-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><Clock className="h-4 w-4" /> Time</Label>
                  <Input
                    type="time"
                    value={editingJob.time_slot || '09:00'}
                    onChange={(e) => setEditingJob({...editingJob, time_slot: e.target.value})}
                    data-testid="edit-job-time"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Duration (hours)</Label>
                  <Select 
                    value={String(editingJob.duration || 2)} 
                    onValueChange={(v) => setEditingJob({...editingJob, duration: parseInt(v)})}
                  >
                    <SelectTrigger data-testid="edit-job-duration">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6,7,8].map(h => (
                        <SelectItem key={h} value={String(h)}>{h} hour{h > 1 ? 's' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select 
                    value={editingJob.status || 'planned'} 
                    onValueChange={(v) => setEditingJob({...editingJob, status: v})}
                  >
                    <SelectTrigger data-testid="edit-job-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="planned">Planned</SelectItem>
                      <SelectItem value="in-progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Notes</Label>
                  <AIWordingButton value={editingJob.notes || ''} onApply={(t) => setEditingJob({...editingJob, notes: t})} context="internal notes about a job" testId="ai-edit-job-notes" />
                </div>
                <Textarea
                  value={editingJob.notes || ''}
                  onChange={(e) => setEditingJob({...editingJob, notes: e.target.value})}
                  placeholder="Internal notes..."
                  className="min-h-[60px]"
                  data-testid="edit-job-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditJobOpen(false)}>Cancel</Button>
            <Button onClick={saveJob} disabled={isSavingJob} data-testid="save-job-btn">
              {isSavingJob && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={isEditCustomerOpen} onOpenChange={setIsEditCustomerOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Edit Customer
            </DialogTitle>
            <DialogDescription>
              Update customer information
            </DialogDescription>
          </DialogHeader>
          {editingCustomer && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={editingCustomer.name}
                  onChange={(e) => setEditingCustomer({...editingCustomer, name: e.target.value})}
                  placeholder="Customer name"
                  data-testid="edit-customer-name"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editingCustomer.email}
                    onChange={(e) => setEditingCustomer({...editingCustomer, email: e.target.value})}
                    placeholder="email@example.com"
                    data-testid="edit-customer-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    type="tel"
                    value={editingCustomer.phone}
                    onChange={(e) => setEditingCustomer({...editingCustomer, phone: e.target.value})}
                    placeholder="07xxx xxxxxx"
                    data-testid="edit-customer-phone"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Address</Label>
                <Textarea
                  value={editingCustomer.address}
                  onChange={(e) => setEditingCustomer({...editingCustomer, address: e.target.value})}
                  placeholder="Full address"
                  className="min-h-[80px]"
                  data-testid="edit-customer-address"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Source</Label>
                <Select 
                  value={editingCustomer.source || 'website'} 
                  onValueChange={(v) => setEditingCustomer({...editingCustomer, source: v})}
                >
                  <SelectTrigger data-testid="edit-customer-source">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="manual">Manual Entry</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Notes</Label>
                  <AIWordingButton value={editingCustomer.notes || ''} onApply={(t) => setEditingCustomer({...editingCustomer, notes: t})} context="internal notes about a customer" testId="ai-edit-customer-notes" />
                </div>
                <Textarea
                  value={editingCustomer.notes || ''}
                  onChange={(e) => setEditingCustomer({...editingCustomer, notes: e.target.value})}
                  placeholder="Internal notes about this customer..."
                  className="min-h-[60px]"
                  data-testid="edit-customer-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="destructive" 
              size="sm"
              onClick={() => editingCustomer && deleteCustomer(editingCustomer.id)}
              className="sm:mr-auto"
              data-testid="delete-customer-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete Customer
            </Button>
            <Button variant="outline" onClick={() => setIsEditCustomerOpen(false)}>Cancel</Button>
            <Button onClick={saveCustomer} disabled={isSavingCustomer} data-testid="save-customer-btn">
              {isSavingCustomer && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pipeline Detail Dialog */}
      <Dialog open={isPipelineDetailOpen} onOpenChange={setIsPipelineDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-start justify-between">
              <div>
                <DialogTitle className="font-heading">{selectedPipelineEnquiry?.name}</DialogTitle>
                <DialogDescription>
                  {serviceLabels[selectedPipelineEnquiry?.service] || selectedPipelineEnquiry?.service} enquiry
                </DialogDescription>
              </div>
              {/* Edit/Delete buttons prominently placed in header */}
              <div className="flex gap-2 ml-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => openEditEnquiry(selectedPipelineEnquiry)}
                  data-testid="edit-enquiry-btn"
                >
                  <Edit3 className="h-4 w-4" />
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => deleteEnquiry(selectedPipelineEnquiry?.id)}
                  data-testid="delete-enquiry-btn"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </DialogHeader>
          {selectedPipelineEnquiry && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p>{selectedPipelineEnquiry.email}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Phone</Label>
                  <p>{selectedPipelineEnquiry.phone || 'N/A'}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Message</Label>
                  <p className="text-sm">{selectedPipelineEnquiry.message}</p>
                </div>
                {selectedPipelineEnquiry.notes && (
                  <div className="col-span-2">
                    <Label className="text-muted-foreground">Notes</Label>
                    <p className="text-sm whitespace-pre-wrap bg-muted/50 p-2 rounded">{selectedPipelineEnquiry.notes}</p>
                  </div>
                )}
              </div>
              
              <div>
                <Label className="text-muted-foreground mb-2 block">Move to Stage</Label>
                <div className="flex flex-wrap gap-2">
                  {pipelineStages.map((stage) => (
                    <Button
                      key={stage.id}
                      size="sm"
                      variant={selectedPipelineEnquiry.status === stage.id || 
                        (stage.id === 'new' && (!selectedPipelineEnquiry.status || selectedPipelineEnquiry.status === 'pending')) 
                        ? "default" : "outline"}
                      onClick={() => updateEnquiryStage(selectedPipelineEnquiry.id, stage.id)}
                      style={{ 
                        borderColor: stage.color,
                        backgroundColor: selectedPipelineEnquiry.status === stage.id ? stage.color : 'transparent',
                        color: selectedPipelineEnquiry.status === stage.id ? 'white' : stage.color
                      }}
                      className="text-xs"
                      data-testid={`stage-btn-${stage.id}`}
                    >
                      {stage.label}
                    </Button>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-2 pt-2 border-t">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setIsNotifyDialogOpen(true)}
                  className="gap-2 flex-1"
                  data-testid="notify-btn"
                >
                  <Mail className="h-4 w-4" />
                  Send Notification
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => { 
                    if (onConvertToCustomer) onConvertToCustomer(selectedPipelineEnquiry.id); 
                    setIsPipelineDetailOpen(false); 
                  }}
                  className="gap-2 flex-1"
                >
                  <UserPlus className="h-4 w-4" />
                  Convert to Customer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Notification Dialog */}
      <Dialog open={isNotifyDialogOpen} onOpenChange={setIsNotifyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Notification</DialogTitle>
            <DialogDescription>
              Manually send an update to the customer or team about this enquiry.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Send To</Label>
              <Select value={notifyType} onValueChange={setNotifyType}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Customer Only</SelectItem>
                  <SelectItem value="team">Team Only</SelectItem>
                  <SelectItem value="both">Both Customer & Team</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Custom Message (Optional)</Label>
                <AIWordingButton value={notifyMessage} onApply={setNotifyMessage} context="a short update message emailed to a customer about their enquiry" testId="ai-notify-message" />
              </div>
              <Textarea 
                placeholder="Add a custom message to include in the notification..."
                value={notifyMessage}
                onChange={(e) => setNotifyMessage(e.target.value)}
                className="mt-1"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsNotifyDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={sendEnquiryNotification}
                disabled={isSendingNotification}
                className="gap-2"
                data-testid="send-notification-btn"
              >
                {isSendingNotification ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                Send Notification
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Enquiry Dialog */}
      <Dialog open={isEditEnquiryOpen} onOpenChange={setIsEditEnquiryOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Edit3 className="h-5 w-5" />
              Edit Enquiry
            </DialogTitle>
            <DialogDescription>
              Update enquiry details for {editingEnquiry?.name}
            </DialogDescription>
          </DialogHeader>
          {editingEnquiry && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={editingEnquiry.name}
                    onChange={(e) => setEditingEnquiry({...editingEnquiry, name: e.target.value})}
                    placeholder="Customer name"
                    data-testid="edit-enquiry-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editingEnquiry.email}
                    onChange={(e) => setEditingEnquiry({...editingEnquiry, email: e.target.value})}
                    placeholder="email@example.com"
                    data-testid="edit-enquiry-email"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    type="tel"
                    value={editingEnquiry.phone}
                    onChange={(e) => setEditingEnquiry({...editingEnquiry, phone: e.target.value})}
                    placeholder="07xxx xxxxxx"
                    data-testid="edit-enquiry-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Service</Label>
                  <Select 
                    value={editingEnquiry.service || ''} 
                    onValueChange={(v) => setEditingEnquiry({...editingEnquiry, service: v})}
                  >
                    <SelectTrigger data-testid="edit-enquiry-service">
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General Handyman</SelectItem>
                      <SelectItem value="carpentry">Carpentry</SelectItem>
                      <SelectItem value="plumbing">Plumbing</SelectItem>
                      <SelectItem value="electrical">Electrical</SelectItem>
                      <SelectItem value="painting">Painting</SelectItem>
                      <SelectItem value="renovation">Renovation</SelectItem>
                      <SelectItem value="garden">Garden Work</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Address</Label>
                <Textarea
                  value={editingEnquiry.address || ''}
                  onChange={(e) => setEditingEnquiry({...editingEnquiry, address: e.target.value})}
                  placeholder="Customer address"
                  className="min-h-[60px]"
                  data-testid="edit-enquiry-address"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Message/Details</Label>
                <Textarea
                  value={editingEnquiry.message}
                  onChange={(e) => setEditingEnquiry({...editingEnquiry, message: e.target.value})}
                  placeholder="Enquiry details..."
                  className="min-h-[80px]"
                  data-testid="edit-enquiry-message"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Internal Notes</Label>
                <Textarea
                  value={editingEnquiry.notes || ''}
                  onChange={(e) => setEditingEnquiry({...editingEnquiry, notes: e.target.value})}
                  placeholder="Notes (not visible to customer)..."
                  className="min-h-[60px]"
                  data-testid="edit-enquiry-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="destructive" 
              size="sm"
              onClick={() => editingEnquiry && deleteEnquiry(editingEnquiry.id)}
              className="sm:mr-auto"
              data-testid="delete-enquiry-from-edit-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete Enquiry
            </Button>
            <Button variant="outline" onClick={() => setIsEditEnquiryOpen(false)}>Cancel</Button>
            <Button onClick={saveEnquiry} disabled={isSavingEnquiry} data-testid="save-enquiry-btn">
              {isSavingEnquiry && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
